import { AuthContext, AuthContextValue } from "@/context/authContext"
import type { AuthTokenPayload } from "@/types"
import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { useIframeMessaging } from "./useIframeMessaging"

interface UseAuthOptions {
  autoRefresh?: boolean;
  refreshThresholdMs?: number;
}

interface UseAuthReturn {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  getToken: () => string | null;
  refreshToken: () => Promise<void>;
}

/**
 * Hook for managing authentication state with iframe token flow
 */
export function useAuth(options: UseAuthOptions = {}): UseAuthReturn {
  const { autoRefresh = true, refreshThresholdMs = 5 * 60 * 1000 } = options // 5 minutes

  const [tokenPayload, setTokenPayload] = useState<AuthTokenPayload | null>(
    null,
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const refreshTimeoutRef = useRef<number | null>(null)
  // Ref to track current valid token - allows getToken to be stable while returning current value
  const tokenRef = useRef<string | null>(null)

  const { waitForAuthToken, isInIframe } = useIframeMessaging()

  // Synchronously update tokenRef during render so child effects
  // can read the current token immediately (avoids useEffect timing race).
  if (tokenPayload && tokenPayload.expiresAt > Date.now()) {
    tokenRef.current = tokenPayload.token
  } else {
    tokenRef.current = null
  }

  // Calculate if token is valid
  const isTokenValid = useCallback(() => {
    if (!tokenPayload) return false

    return tokenPayload.expiresAt > Date.now()
  }, [tokenPayload])

  // Get current token - stable function that reads from ref
  // This avoids stale closure issues when captured by singletons like ApiClient
  const getToken = useCallback(() => {
    return tokenRef.current
  }, [])

  // Request and set new token
  const refreshToken = useCallback(async () => {
    if (!isInIframe) {
      setError("Not running inside an iframe, cannot obtain auth token")
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const payload = await waitForAuthToken(10000) // 10 second timeout
      setTokenPayload(payload)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to get auth token"
      setError(message)
      console.error("Auth token error:", message)
    } finally {
      setIsLoading(false)
    }
  }, [isInIframe, waitForAuthToken])

  // Schedule token refresh before expiration
  const scheduleRefresh = useCallback(() => {
    if (!autoRefresh || !tokenPayload) return

    // Clear existing timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    // Calculate when to refresh (before expiration threshold)
    const msUntilExpiry = tokenPayload.expiresAt - Date.now()
    const refreshIn = Math.max(0, msUntilExpiry - refreshThresholdMs)

    if (refreshIn > 0) {
      refreshTimeoutRef.current = window.setTimeout(() => {
        refreshToken()
      }, refreshIn)
    } else if (msUntilExpiry > 0) {
      // Token will expire within threshold, refresh now
      refreshToken()
    }
  }, [autoRefresh, tokenPayload, refreshThresholdMs, refreshToken])

  // Initial token fetch
  useEffect(() => {
    refreshToken()
  }, [refreshToken])

  // Schedule refresh when token changes
  useEffect(() => {
    scheduleRefresh()

    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
    }
  }, [scheduleRefresh])

  return {
    token: tokenPayload?.token ?? null,
    isAuthenticated: isTokenValid(),
    isLoading,
    error,
    getToken,
    refreshToken,
  }
}

/**
 * Hook to access authentication context.
 * Must be used within an AuthProvider.
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider")
  }
  return context
}
